import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../contractorApi';
import { storageBaseUrl } from '../../api';

// ── SVGアイコン ──────────────────────────────────────────────
const FileTextIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
);

const ImageIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
    </svg>
);

const XIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

// ── スタイル定数 ─────────────────────────────────────────────
const CARD = { background: '#fff', borderRadius: 8, padding: '24px', marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' };
const H2 = { fontSize: '1.2rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 24px' };
const H2_SUB = { fontSize: '1.2rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 6px' };
const SUB = { fontSize: '0.875rem', color: '#6b7280', margin: '0 0 20px' };
const LBL = { display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#1a1a2e', marginBottom: 8 };
const FIELD = { marginBottom: 20 };
const INPUT = {
    width: '100%', padding: '10px 16px', fontSize: '0.9rem',
    border: '1px solid #e0e4ea', borderRadius: 4, outline: 'none',
    boxSizing: 'border-box', color: '#1a1a2e', background: '#fff', transition: 'border-color 0.15s',
};

// ── メインコンポーネント ────────────────────────────────────
const ContractorProfilePage = () => {
    const contractorId = localStorage.getItem('contractor_id');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // 基本情報
    const [companyName, setCompanyName] = useState('');
    const [email, setEmail] = useState('');
    const [representative, setRepresentative] = useState('');
    const [phone, setPhone] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [prefecture, setPrefecture] = useState('');
    const [city, setCity] = useState('');
    const [address, setAddress] = useState('');
    const [description, setDescription] = useState('');
    const [constructionLicenseNumber, setConstructionLicenseNumber] = useState('');
    const [invoiceRegistrationNumber, setInvoiceRegistrationNumber] = useState('');
    const [qualifications, setQualifications] = useState(['']);

    // 銀行口座情報
    const [bankName, setBankName] = useState('');
    const [bankCode, setBankCode] = useState('');
    const [branchName, setBranchName] = useState('');
    const [branchCode, setBranchCode] = useState('');
    const [accountType, setAccountType] = useState('普通');
    const [accountNumber, setAccountNumber] = useState('');
    const [accountHolder, setAccountHolder] = useState('');

    // 画像
    const [certImageUrl, setCertImageUrl] = useState(null);
    const [certImageFile, setCertImageFile] = useState(null);
    const [certImagePreview, setCertImagePreview] = useState(null);
    const fileInputRef = useRef(null);
    const [sealImageUrl, setSealImageUrl] = useState(null);
    const [sealImageFile, setSealImageFile] = useState(null);
    const [sealImagePreview, setSealImagePreview] = useState(null);
    const sealImageRef = useRef(null);

    // 対応エリア
    const [prefectures, setPrefectures] = useState([]);          // [{id, name}]
    const [selectedPrefId, setSelectedPrefId] = useState(null);  // 表示中の都道府県ID
    const [availableCities, setAvailableCities] = useState([]);  // [{id, name}] その都道府県の市区町村
    const [citiesLoading, setCitiesLoading] = useState(false);
    const [selectedCities, setSelectedCities] = useState([]);    // [{id, name, prefecture}] 選択済み

    // 都道府県一覧を読み込む
    useEffect(() => {
        api.get('service_area_prefectures').then(r => {
            setPrefectures(r.data);
            if (!selectedPrefId && r.data.length > 0) setSelectedPrefId(r.data[0].id);
        }).catch(console.error);
    }, []); // eslint-disable-line

    // 選択中の都道府県の市区町村を読み込む
    const loadCities = useCallback((prefId) => {
        if (!prefId) return;
        setCitiesLoading(true);
        api.get(`service_area_cities?prefecture_id=${prefId}`)
            .then(r => setAvailableCities(r.data))
            .catch(console.error)
            .finally(() => setCitiesLoading(false));
    }, []);

    useEffect(() => {
        if (selectedPrefId) loadCities(selectedPrefId);
    }, [selectedPrefId, loadCities]);

    // 業者データを読み込む
    useEffect(() => {
        api.get(`contractors/${contractorId}`)
            .then(r => {
                const c = r.data;
                setCompanyName(c.company_name || '');
                setEmail(c.email || '');
                setRepresentative(c.representative || '');
                setPhone(c.phone || '');
                setPostalCode(c.postal_code || '');
                setPrefecture(c.prefecture || '');
                setCity(c.city || '');
                setAddress(c.address || '');
                setDescription(c.description || '');
                setConstructionLicenseNumber(c.construction_license_number || '');
                setInvoiceRegistrationNumber(c.invoice_registration_number || '');
                const quals = (c.qualification_certificate || '').split('\n').filter(Boolean);
                setQualifications(quals.length > 0 ? quals : ['']);
                setBankName(c.bank_name || '');
                setBankCode(c.bank_code || '');
                setBranchName(c.branch_name || '');
                setBranchCode(c.branch_code || '');
                setAccountType(c.account_type || '普通');
                setAccountNumber(c.account_number || '');
                setAccountHolder(c.account_holder || '');
                if (c.qualification_certificate_image) {
                    setCertImageUrl(`${storageBaseUrl}/${c.qualification_certificate_image}`);
                }
                if (c.seal_image) {
                    setSealImageUrl(`${storageBaseUrl}/${c.seal_image}`);
                }
                if (c.service_areas?.length) {
                    setSelectedCities(c.service_areas);
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [contractorId]);

    const toggleCity = (city) => {
        const pref = prefectures.find(p => p.id === selectedPrefId);
        setSelectedCities(prev =>
            prev.some(c => c.id === city.id)
                ? prev.filter(c => c.id !== city.id)
                : [...prev, { ...city, prefecture: pref }]
        );
    };

    const removeCity = (cityId) => {
        setSelectedCities(prev => prev.filter(c => c.id !== cityId));
    };

    // 優先順位の入れ替え（配列の並び順＝優先順位として保存される）
    const moveCity = (index, direction) => {
        setSelectedCities(prev => {
            const next = [...prev];
            const target = index + direction;
            if (target < 0 || target >= next.length) return prev;
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    const selectAllInCurrentPref = () => {
        const pref = prefectures.find(p => p.id === selectedPrefId);
        setSelectedCities(prev => {
            const currentIds = new Set(prev.map(c => c.id));
            const toAdd = availableCities
                .filter(c => !currentIds.has(c.id))
                .map(c => ({ ...c, prefecture: pref }));
            return [...prev, ...toAdd];
        });
    };

    const deselectAllInCurrentPref = () => {
        setSelectedCities(prev => prev.filter(c => c.prefecture?.id !== selectedPrefId));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        setSuccess(false);

        try {
            // プロフィール保存
            const formData = new FormData();
            formData.append('company_name', companyName);
            formData.append('email', email);
            formData.append('representative', representative);
            formData.append('phone', phone);
            formData.append('postal_code', postalCode);
            formData.append('prefecture', prefecture);
            formData.append('city', city);
            formData.append('address', address);
            formData.append('description', description);
            formData.append('construction_license_number', constructionLicenseNumber);
            formData.append('invoice_registration_number', invoiceRegistrationNumber);
            formData.append('qualification_certificate', qualifications.filter(q => q.trim()).join('\n'));
            if (certImageFile) formData.append('qualification_certificate_image', certImageFile);
            if (sealImageFile) formData.append('seal_image', sealImageFile);
            formData.append('bank_name', bankName);
            formData.append('bank_code', bankCode);
            formData.append('branch_name', branchName);
            formData.append('branch_code', branchCode);
            formData.append('account_type', accountType);
            formData.append('account_number', accountNumber);
            formData.append('account_holder', accountHolder);
            formData.append('_method', 'PUT');
            await api.post(`contractors/${contractorId}`, formData);

            // 対応エリア保存
            await api.post(`contractors/${contractorId}/service_areas`, {
                city_ids: selectedCities.map(c => c.id),
            });

            setSuccess(true);
            setCertImageFile(null);
            setSealImageFile(null);
            window.scrollTo(0, 0);
        } catch {
            setError('保存に失敗しました。');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    const displayCertImage = certImagePreview || certImageUrl;
    const displaySealImage = sealImagePreview || sealImageUrl;

    return (
        <div style={{ minHeight: '100vh', background: '#f5f6f8' }}>
            <style>{`
                .pf-inp:focus { border-color: #005c53 !important; }
                .pf-upload:hover { border-color: #005c53 !important; }
                .pf-save-btn:hover:not(:disabled) { background: #004940 !important; }
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>

            <div style={{ maxWidth: 896, margin: '0 auto', padding: '32px 24px 64px' }}>
                <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 32px' }}>
                    プロフィール設定
                </h1>

                {success && (
                    <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 6, padding: '12px 16px', marginBottom: 24, color: '#065f46', fontSize: '0.9rem' }}>
                        保存しました。
                    </div>
                )}
                {error && (
                    <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '12px 16px', marginBottom: 24, color: '#991b1b', fontSize: '0.9rem' }}>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>

                    {/* ── 基本情報 ────────────────────────────────── */}
                    <div style={CARD}>
                        <h2 style={H2}>基本情報</h2>

                        <div style={FIELD}>
                            <label style={LBL}>会社名 <span style={{ color: '#ef4444' }}>*</span></label>
                            <input className="pf-inp" style={INPUT} value={companyName} onChange={e => setCompanyName(e.target.value)} required />
                        </div>
                        <div style={FIELD}>
                            <label style={LBL}>メールアドレス <span style={{ color: '#ef4444' }}>*</span></label>
                            <input className="pf-inp" style={INPUT} type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                        </div>
                        <div style={FIELD}>
                            <label style={LBL}>代表者名 <span style={{ color: '#ef4444' }}>*</span></label>
                            <input className="pf-inp" style={INPUT} value={representative} onChange={e => setRepresentative(e.target.value)} required />
                        </div>
                        <div style={FIELD}>
                            <label style={LBL}>電話番号 <span style={{ color: '#ef4444' }}>*</span></label>
                            <input className="pf-inp" style={INPUT} type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="03-1234-5678" required />
                        </div>

                        <div style={{ ...FIELD, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                                <label style={LBL}>都道府県</label>
                                <select className="pf-inp" style={{ ...INPUT, appearance: 'auto' }} value={prefecture} onChange={e => setPrefecture(e.target.value)}>
                                    <option value="">選択してください</option>
                                    {prefectures.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={LBL}>市区町村</label>
                                <input className="pf-inp" style={INPUT} value={city} onChange={e => setCity(e.target.value)} placeholder="渋谷区" />
                            </div>
                        </div>

                        <div style={FIELD}>
                            <label style={LBL}>番地・建物名</label>
                            <input className="pf-inp" style={INPUT} value={address} onChange={e => setAddress(e.target.value)} placeholder="道玄坂1-2-3 渋谷ビル5F" />
                        </div>
                        <div>
                            <label style={LBL}>郵便番号</label>
                            <input className="pf-inp" style={{ ...INPUT, maxWidth: 200 }} value={postalCode} onChange={e => setPostalCode(e.target.value)} placeholder="1500043" />
                        </div>

                        <div style={{ ...FIELD, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
                            <div>
                                <label style={LBL}>建設業許可番号</label>
                                <input className="pf-inp" style={INPUT} value={constructionLicenseNumber} onChange={e => setConstructionLicenseNumber(e.target.value)} placeholder="愛知県知事許可（般-6）第123456号" />
                            </div>
                            <div>
                                <label style={LBL}>インボイス登録番号</label>
                                <input className="pf-inp" style={INPUT} value={invoiceRegistrationNumber} onChange={e => setInvoiceRegistrationNumber(e.target.value)} placeholder="T1234567890123" />
                            </div>
                        </div>
                    </div>

                    {/* ── 自己紹介 ────────────────────────────────── */}
                    <div style={CARD}>
                        <h2 style={H2_SUB}>自己紹介</h2>
                        <p style={SUB}>会社紹介・アピールポイントをご記入ください</p>
                        <textarea
                            className="pf-inp"
                            rows={6}
                            placeholder="会社の特徴、強み、実績などをご記入ください"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            style={{ ...INPUT, resize: 'none', minHeight: 120 }}
                        />
                    </div>

                    {/* ── 対応エリア ──────────────────────────────── */}
                    <div style={CARD}>
                        <h2 style={H2_SUB}>対応エリア</h2>
                        <p style={SUB}>工事対応可能なエリアを選択してください</p>

                        <div style={FIELD}>
                            <label style={LBL}>都道府県を選択</label>
                            <select
                                className="pf-inp"
                                style={{ ...INPUT, appearance: 'auto' }}
                                value={selectedPrefId || ''}
                                onChange={e => setSelectedPrefId(Number(e.target.value))}
                            >
                                {prefectures.map(p => {
                                    const cnt = selectedCities.filter(c => c.prefecture?.id === p.id).length;
                                    return <option key={p.id} value={p.id}>{p.name}{cnt > 0 ? ` (${cnt}件)` : ''}</option>;
                                })}
                            </select>
                        </div>

                        {selectedCities.length > 0 && (
                            <div style={FIELD}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <label style={{ ...LBL, marginBottom: 0 }}>選択中のエリア（{selectedCities.length}件）</label>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedCities([])}
                                        style={{ padding: '4px 12px', background: 'none', border: '1px solid #fca5a5', borderRadius: 4, fontSize: '0.8rem', color: '#ef4444', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    >
                                        全て解除
                                    </button>
                                </div>
                                <p style={{ ...SUB, margin: '0 0 8px' }}>上にあるエリアほど優先度が高くなります。矢印で並び替えできます</p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {selectedCities.map((c, i) => (
                                        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px', background: '#f5f6f8', border: '1px solid #e0e4ea', borderRadius: 8 }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: '#005c53', color: '#fff', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>
                                                {i + 1}
                                            </span>
                                            <span style={{ flex: 1, fontSize: '0.85rem', color: '#1a1a2e' }}>{c.prefecture?.name || ''} {c.name}</span>
                                            <button type="button" onClick={() => moveCity(i, -1)} disabled={i === 0}
                                                style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? '#d1d5db' : '#6b7280', padding: 2 }}>▲</button>
                                            <button type="button" onClick={() => moveCity(i, 1)} disabled={i === selectedCities.length - 1}
                                                style={{ background: 'none', border: 'none', cursor: i === selectedCities.length - 1 ? 'default' : 'pointer', color: i === selectedCities.length - 1 ? '#d1d5db' : '#6b7280', padding: 2 }}>▼</button>
                                            <button type="button" onClick={() => removeCity(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2, display: 'flex', alignItems: 'center' }}>
                                                <XIcon />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                <label style={{ ...LBL, marginBottom: 0 }}>
                                    {prefectures.find(p => p.id === selectedPrefId)?.name || ''}の市区町村
                                    {selectedCities.filter(c => c.prefecture?.id === selectedPrefId).length > 0 && (
                                        <span style={{ color: '#005c53', marginLeft: 8, fontSize: '0.8rem', fontWeight: 400 }}>
                                            ({selectedCities.filter(c => c.prefecture?.id === selectedPrefId).length}件選択中)
                                        </span>
                                    )}
                                </label>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                        type="button"
                                        onClick={selectAllInCurrentPref}
                                        disabled={citiesLoading || availableCities.length === 0}
                                        style={{ padding: '4px 12px', background: '#005c53', border: 'none', borderRadius: 4, fontSize: '0.8rem', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', opacity: (citiesLoading || availableCities.length === 0) ? 0.5 : 1 }}
                                    >
                                        全て選択
                                    </button>
                                    <button
                                        type="button"
                                        onClick={deselectAllInCurrentPref}
                                        disabled={citiesLoading}
                                        style={{ padding: '4px 12px', background: 'none', border: '1px solid #e0e4ea', borderRadius: 4, fontSize: '0.8rem', color: '#6b7280', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    >
                                        全て解除
                                    </button>
                                </div>
                            </div>
                            {citiesLoading ? (
                                <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>読み込み中...</p>
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 220, overflowY: 'auto', padding: 12, border: '1px solid #e0e4ea', borderRadius: 4 }}>
                                    {availableCities.map(c => {
                                        const selected = selectedCities.some(s => s.id === c.id);
                                        return (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onClick={() => toggleCity(c)}
                                                style={{
                                                    padding: '6px 14px', borderRadius: 4, border: 'none',
                                                    fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer',
                                                    background: selected ? '#005c53' : '#f5f6f8',
                                                    color: selected ? '#fff' : '#1a1a2e',
                                                    transition: 'background 0.12s',
                                                }}
                                            >
                                                {c.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── 保有資格 ────────────────────────────────── */}
                    <div style={CARD}>
                        <h2 style={H2}>保有資格</h2>

                        <div style={FIELD}>
                            <label style={LBL}>資格名</label>
                            {qualifications.map((q, i) => (
                                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                    <input
                                        className="pf-inp"
                                        style={{ ...INPUT, flex: 1 }}
                                        value={q}
                                        onChange={e => {
                                            const next = [...qualifications];
                                            next[i] = e.target.value;
                                            setQualifications(next);
                                        }}
                                        placeholder="例：一級建築士"
                                    />
                                    {qualifications.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => setQualifications(qualifications.filter((_, idx) => idx !== i))}
                                            style={{ padding: '0 10px', background: 'none', border: '1px solid #e0e4ea', borderRadius: 4, cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center' }}
                                        >
                                            <XIcon />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => setQualifications([...qualifications, ''])}
                                style={{ padding: '8px 16px', background: '#f5f6f8', border: '1px solid #e0e4ea', borderRadius: 4, fontSize: '0.875rem', color: '#005c53', cursor: 'pointer', fontWeight: 500 }}
                            >
                                + 資格を追加
                            </button>
                        </div>

                        <div>
                            <label style={LBL}>資格証画像</label>
                            {displayCertImage ? (
                                <div>
                                    <img src={displayCertImage} alt="資格証" style={{ maxWidth: '100%', maxHeight: 240, objectFit: 'contain', borderRadius: 6, border: '1px solid #e0e4ea', display: 'block', marginBottom: 8 }} />
                                    <button type="button" onClick={() => { setCertImageFile(null); setCertImagePreview(null); setCertImageUrl(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '0.85rem', padding: 0 }}>
                                        画像を削除
                                    </button>
                                </div>
                            ) : (
                                <div className="pf-upload" onClick={() => fileInputRef.current?.click()}
                                    style={{ border: '2px dashed #e0e4ea', borderRadius: 8, padding: 24, textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.15s' }}>
                                    <div style={{ display: 'inline-flex', padding: 12, background: '#f5f6f8', borderRadius: '50%', marginBottom: 12, color: '#6b7280' }}>
                                        <FileTextIcon />
                                    </div>
                                    <p style={{ fontWeight: 500, color: '#1a1a2e', margin: '0 0 4px', fontSize: '0.9rem' }}>資格証画像をアップロード</p>
                                    <p style={{ color: '#6b7280', fontSize: '0.78rem', margin: 0 }}>PNG, JPG（最大10MB）</p>
                                </div>
                            )}
                            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; if (f) { setCertImageFile(f); setCertImagePreview(URL.createObjectURL(f)); } }} />
                        </div>
                    </div>

                    {/* ── 印鑑画像 ────────────────────────────────── */}
                    <div style={CARD}>
                        <h2 style={H2_SUB}>印鑑画像</h2>
                        <p style={SUB}>見積書・請求書に使用する印鑑画像をアップロードしてください</p>

                        {displaySealImage ? (
                            <div style={{ textAlign: 'center' }}>
                                <img src={displaySealImage} alt="印鑑" style={{ maxWidth: 200, maxHeight: 200, objectFit: 'contain', borderRadius: 6, border: '1px solid #e0e4ea', marginBottom: 8 }} />
                                <br />
                                <button type="button" onClick={() => { setSealImageFile(null); setSealImagePreview(null); setSealImageUrl(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '0.85rem', padding: 0 }}>
                                    画像を削除
                                </button>
                            </div>
                        ) : (
                            <div className="pf-upload" onClick={() => sealImageRef.current?.click()}
                                style={{ border: '2px dashed #e0e4ea', borderRadius: 8, padding: 32, textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.15s' }}>
                                <div style={{ display: 'inline-flex', padding: 16, background: '#f5f6f8', borderRadius: '50%', marginBottom: 16, color: '#6b7280' }}>
                                    <ImageIcon />
                                </div>
                                <p style={{ fontWeight: 500, color: '#1a1a2e', margin: '0 0 6px', fontSize: '1rem' }}>印鑑画像をアップロード</p>
                                <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: 0 }}>PNG, JPG（背景透過推奨・最大5MB）</p>
                            </div>
                        )}
                        <input ref={sealImageRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; if (f) { setSealImageFile(f); setSealImagePreview(URL.createObjectURL(f)); } }} />
                    </div>

                    {/* ── 振込先口座情報 ──────────────────────────── */}
                    <div style={CARD}>
                        <h2 style={H2_SUB}>振込先口座情報</h2>
                        <p style={SUB}>工事完了後の報酬振込先をご登録ください</p>

                        <div style={{ ...FIELD, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                                <label style={LBL}>銀行名</label>
                                <input className="pf-inp" style={INPUT} value={bankName} onChange={e => setBankName(e.target.value)} placeholder="例：三菱UFJ銀行" />
                            </div>
                            <div>
                                <label style={LBL}>金融機関コード</label>
                                <input className="pf-inp" style={INPUT} value={bankCode} onChange={e => setBankCode(e.target.value)} placeholder="例：0005" maxLength={4} />
                            </div>
                        </div>

                        <div style={{ ...FIELD, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                                <label style={LBL}>支店名</label>
                                <input className="pf-inp" style={INPUT} value={branchName} onChange={e => setBranchName(e.target.value)} placeholder="例：渋谷支店" />
                            </div>
                            <div>
                                <label style={LBL}>支店コード</label>
                                <input className="pf-inp" style={INPUT} value={branchCode} onChange={e => setBranchCode(e.target.value)} placeholder="例：001" maxLength={3} />
                            </div>
                        </div>

                        <div style={{ ...FIELD, display: 'grid', gridTemplateColumns: '160px 1fr', gap: 16 }}>
                            <div>
                                <label style={LBL}>口座種別</label>
                                <select className="pf-inp" style={{ ...INPUT, appearance: 'auto' }} value={accountType} onChange={e => setAccountType(e.target.value)}>
                                    <option value="普通">普通</option>
                                    <option value="当座">当座</option>
                                </select>
                            </div>
                            <div>
                                <label style={LBL}>口座番号</label>
                                <input className="pf-inp" style={INPUT} value={accountNumber} onChange={e => setAccountNumber(e.target.value)} placeholder="例：1234567" maxLength={7} />
                            </div>
                        </div>

                        <div>
                            <label style={LBL}>口座名義（カタカナ）</label>
                            <input className="pf-inp" style={INPUT} value={accountHolder} onChange={e => setAccountHolder(e.target.value)} placeholder="例：ヤマシロドボク カブシキガイシャ" />
                        </div>
                    </div>

                    {/* ── 保存ボタン ─────────────────────────────── */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            type="submit"
                            className="pf-save-btn"
                            disabled={saving}
                            style={{
                                padding: '12px 32px', background: saving ? '#9ca3af' : '#005c53',
                                color: '#fff', border: 'none', borderRadius: 4,
                                fontWeight: 500, fontSize: '1rem',
                                cursor: saving ? 'default' : 'pointer',
                                transition: 'background 0.15s', minWidth: 160,
                            }}
                        >
                            {saving ? '保存中...' : '変更を保存する'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ContractorProfilePage;
